import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { GOOGLE_ADS_ID, GA4_ID, ANALYTICS_ENABLED } from "@/lib/gtag";
import { localeFromSearch } from "@/i18n";
import { Layout } from "@/components/Layout";

// Self-hosted fonts — the @font-face CSS is @import'ed at the top of
// src/styles.css so the browser fetches ONE render-blocking stylesheet
// instead of two (Vite used to emit these as a separate index-*.css).
// Import the actual woff2 URL so we can <link rel="preload"> it below —
// Instrument Serif is used in H1s (LCP-adjacent) and was blocking ~170ms.
import instrumentSerifWoff2 from "@fontsource/instrument-serif/files/instrument-serif-latin-400-normal.woff2?url";

// Toaster is only mounted for user feedback (add-to-cart, form errors) —
// defer the ~13 kB gzip sonner runtime until after hydration so it doesn't
// block LCP on the first paint.
import { lazy, Suspense } from "react";
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

function NotFoundComponent() {
  return (
    <Layout>
      <section className="container-prose flex min-h-[60vh] flex-col items-center justify-center px-4 py-20 text-center">
        <span className="text-xs uppercase tracking-[0.28em] text-muted-foreground">404</span>
        <h1 className="mt-4 font-display text-5xl italic text-foreground md:text-6xl">
          Sivua ei löytynyt
        </h1>
        <p className="mt-4 max-w-md text-sm text-muted-foreground md:text-base">
          Etsimääsi sivua ei ole olemassa tai se on siirretty. Palaa etusivulle tai
          jatka selaamaan mallistoa.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-background transition-transform hover:-translate-y-0.5"
          >
            Etusivulle
          </Link>
          <Link
            to="/kauppa"
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-foreground transition-colors hover:border-foreground"
          >
            Selaa mallistoa →
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Sivu ei latautunut
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Jokin meni vikaan. Voit yrittää uudelleen tai palata etusivulle.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Yritä uudelleen
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Etusivulle
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Montessori24" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "fi_FI" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "idGw_QSg3CnlHUYtTPj_11qnDjO9q3nqy5fiDKZ-LFs" },
      { name: "theme-color", content: "#FAF8F5" },
      { name: "format-detection", content: "telephone=no" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://cdn.shopify.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://cdn.shopify.com" },
      // Storefront API endpoint we hit from the client after hydration
      // (rail refetches, product detail, cart). Preconnect saves ~150ms TLS
      // on mobile before the first request lands.
      { rel: "preconnect", href: "https://suomi-soft-play-epz24.myshopify.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://suomi-soft-play-epz24.myshopify.com" },
      // Preload only the display serif used in H1s (LCP-adjacent text).
      // Keep it at default priority so it doesn't outrank the hero image
      // preload on the network queue. Work Sans (body) is not
      // preloaded — it's not on the LCP path and was competing with
      // the hero image bytes on 3G/4G.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: instrumentSerifWoff2,
        crossOrigin: "anonymous",
      },
      // NOTE: hreflang alternates are declared per-route (leaves only).
      // Emitting them here concatenates the homepage's fi=/ + en-US=/us
      // into every subpage — a wrong signal that made Google treat
      // subpages as duplicates of /. Homepage alternates live in
      // src/routes/index.tsx; /us has its own; other pages self-declare.
    ],
    scripts: [
      ...(ANALYTICS_ENABLED
        ? [
            {
              children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);} window.gtag = gtag;
gtag('js', new Date());
// Google Consent Mode v2 — every storage starts DENIED. GA4 then runs in
// cookieless mode and no identifiers are stored. The stored choice from the
// cookie banner (CookieConsent.tsx, key "m24-cookie-consent-v1") upgrades the
// defaults before 'config' below; later choices arrive via the
// "m24:consent-change" event and are applied with a consent 'update'.
var m24Consent = { analytics: false, marketing: false };
try {
  var m24Stored = JSON.parse(window.localStorage.getItem('m24-cookie-consent-v1') || 'null');
  if (m24Stored && m24Stored.version === 1) {
    m24Consent = { analytics: m24Stored.analytics === true, marketing: m24Stored.marketing === true };
  }
} catch (e) {}
function m24ConsentState(c) {
  return {
    analytics_storage: c.analytics ? 'granted' : 'denied',
    ad_storage: c.marketing ? 'granted' : 'denied',
    ad_user_data: c.marketing ? 'granted' : 'denied',
    ad_personalization: c.marketing ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted'
  };
}
gtag('consent', 'default', m24ConsentState(m24Consent));
window.addEventListener('m24:consent-change', function (e) {
  var d = (e && e.detail) || {};
  gtag('consent', 'update', m24ConsentState({ analytics: d.analytics === true, marketing: d.marketing === true }));
});
${GOOGLE_ADS_ID ? `gtag('config', '${GOOGLE_ADS_ID}');` : ""}
${GA4_ID ? `gtag('config', '${GA4_ID}', { anonymize_ip: true });` : ""}
// Defer the ~40 kB gtag.js library until the visitor first interacts with
// the page (or 5 s after load as a fallback): the queue above buffers
// events, and everything flushes when the library lands. Keeps third-party
// JS off the critical rendering path AND out of the initial main-thread
// trace (Lighthouse TBT / "unused JavaScript" findings).
var gtagLibLoaded = false;
function loadGtagLib() {
  if (gtagLibLoaded) return;
  gtagLibLoaded = true;
  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID || GA4_ID}';
  document.head.appendChild(s);
}
['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(function (evt) {
  window.addEventListener(evt, loadGtagLib, { once: true, passive: true });
});
window.addEventListener('load', function () {
  setTimeout(loadGtagLib, 5000);
});`,
            },
          ]
        : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Montessori24",
          legalName: "MLR Solutions OY",
          url: "https://montessori24.fi",
          logo: "https://montessori24.fi/og-image.jpg",
          email: "asiakaspalvelu@montessori24.fi",
          taxID: "3582909-7",
          areaServed: "FI",
          contactPoint: [{
            "@type": "ContactPoint",
            email: "asiakaspalvelu@montessori24.fi",
            contactType: "customer service",
            areaServed: "FI",
            availableLanguage: ["Finnish", "fi"],
          }],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Montessori24",
          url: "https://montessori24.fi",
          inLanguage: "fi-FI",
          publisher: { "@type": "Organization", name: "Montessori24" },
          potentialAction: {
            "@type": "SearchAction",
            target: "https://montessori24.fi/kauppa?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = pathname === "/us" || pathname.startsWith("/us/")
    ? "en"
    : localeFromSearch(searchStr);
  // If the URL carries a ?lang=en|sv|et override, tell crawlers not to
  // index this variant — the canonical Finnish URL is the one Google
  // should rank. Without this, `?lang=et` copies of pages like
  // /vaaterekki compete with the real FI page in the SERP.
  const hasLangOverride = /[?&]lang=(en|sv|et)\b/.test(searchStr);
  return (
    <html lang={locale}>
      <head>
        <HeadContent />
        {hasLangOverride && <meta name="robots" content="noindex, follow" />}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Suspense fallback={null}>
        <Toaster position="top-center" richColors closeButton />
      </Suspense>
    </QueryClientProvider>
  );
}
